/* =========================================================================
   Product card gallery + cambio de color (Origo)

   Scroll-snap nativo: el swipe lo hace el navegador. Este archivo sólo:
     1. sincroniza el índice visible con las flechas,
     2. mueve el track con las flechas,
     3. evita que un swipe se interprete como click al link del producto,
     4. cambia la tarjeta completa al tocar un swatch de color.

   Sin librerías y sin listeners de scroll globales.
   ========================================================================= */

(function () {
  'use strict';

  var DRAG_THRESHOLD = 8; // px de movimiento antes de considerarlo swipe

  function escAttr(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  /* -----------------------------------------------------------------------
     1. Galería
     -----------------------------------------------------------------------

     `slides` se vuelve a leer con `refresh()` en vez de quedarse cacheada en
     el closure: al cambiar de color el track se reconstruye y el número de
     fotos puede ser distinto entre un color y otro. Los listeners viven en
     el TRACK (no en los slides), así que sobreviven a la reconstrucción.
     ----------------------------------------------------------------------- */

  function initGallery(track) {
    if (track.dataset.pcGalleryReady === 'true') return;
    track.dataset.pcGalleryReady = 'true';

    var root = track.closest('.pc-gallery');
    if (!root) return;

    var prevButton = root.querySelector('[data-pc-prev]');
    var nextButton = root.querySelector('[data-pc-next]');
    var slides = [];
    var current = -1;
    var slideWidth = track.clientWidth;

    function readSlides() {
      slides = Array.prototype.slice.call(track.querySelectorAll('[data-pc-slide]'));
    }

    function setActive(index) {
      if (index === current) return;
      current = index;

      // Sólo la imagen visible es tabulable
      slides.forEach(function (slide, i) {
        if (i === index) {
          slide.removeAttribute('tabindex');
        } else {
          slide.setAttribute('tabindex', '-1');
        }
      });

      if (prevButton) prevButton.disabled = index <= 0;
      if (nextButton) nextButton.disabled = index >= slides.length - 1;
    }

    // --- Índice visible, sincronizado con el scroll ---
    // Sale de la posición de scroll y no de un IntersectionObserver: los slides
    // miden exactamente el 100% del track, así que la división es exacta, no
    // hay thresholds que afinar y funciona igual en todos los navegadores.
    function syncFromScroll() {
      if (!slideWidth) return;
      var index = Math.round(track.scrollLeft / slideWidth);
      if (index < 0) index = 0;
      if (index > slides.length - 1) index = slides.length - 1;
      setActive(index);
    }

    track.addEventListener('scroll', syncFromScroll, { passive: true });

    if ('ResizeObserver' in window) {
      new ResizeObserver(function () {
        slideWidth = track.clientWidth;
      }).observe(track);
    } else {
      window.addEventListener('resize', function () {
        slideWidth = track.clientWidth;
      });
    }

    // --- Flechas ---
    function scrollByOne(direction) {
      track.scrollBy({ left: direction * slideWidth, behavior: 'smooth' });
    }

    function bindNav(button, direction) {
      if (!button) return;
      button.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        scrollByOne(direction);
      });
    }

    bindNav(prevButton, -1);
    bindNav(nextButton, 1);

    // --- Swipe vs click ---
    // La card entera es un link. Sin esto, arrastrar para ver otra foto
    // navegaría al producto al soltar.
    var startX = 0;
    var startY = 0;
    var dragged = false;

    track.addEventListener(
      'pointerdown',
      function (event) {
        startX = event.clientX;
        startY = event.clientY;
        dragged = false;
      },
      { passive: true }
    );

    track.addEventListener(
      'pointermove',
      function (event) {
        if (dragged) return;
        var dx = Math.abs(event.clientX - startX);
        var dy = Math.abs(event.clientY - startY);
        if (dx > DRAG_THRESHOLD || dy > DRAG_THRESHOLD) dragged = true;
      },
      { passive: true }
    );

    track.addEventListener('click', function (event) {
      if (!dragged) return;
      event.preventDefault();
      dragged = false;
    });

    // Vuelve a leer los slides y regresa a la primera foto. Lo llama el
    // cambio de color después de reconstruir el track.
    function refresh() {
      readSlides();
      slideWidth = track.clientWidth;
      track.scrollLeft = 0;
      current = -1;
      setActive(0);
    }

    track.pcGallery = { refresh: refresh };
    refresh();
  }

  /* -----------------------------------------------------------------------
     2. Cambio de color en el lugar
     -----------------------------------------------------------------------

     Al tocar un swatch se cambia TODA la tarjeta —foto, galería, título,
     material, precio, estrellas y a dónde lleva el clic— sin recargar.

     En Origo cada color es un producto distinto: si sólo se cambiara la foto,
     la tarjeta mostraría el zapato de un color con el precio y las reseñas de
     otro.

     El `<a>` del swatch se queda intacto. Este código sólo intercepta el clic
     izquierdo simple: ctrl/cmd/shift/alt y el botón central siguen abriendo el
     producto en pestaña nueva, y si el JSON falta o falla el parseo, el link
     navega como siempre. Degrada, no se rompe.
     ----------------------------------------------------------------------- */

  function initSwatches(card) {
    if (card.dataset.pcColorReady === 'true') return;

    var wrapper = card.querySelector('.product-card-swatches');
    if (!wrapper) return;

    var payload = wrapper.querySelector('[data-pc-colors]');
    if (!payload) return;

    var colores;
    try {
      colores = JSON.parse(payload.textContent);
    } catch (error) {
      return; // sin datos utilizables: los swatches siguen siendo links normales
    }
    if (!colores || !colores.length) return;

    card.dataset.pcColorReady = 'true';

    var porId = {};
    colores.forEach(function (color) {
      porId[String(color.id)] = color;
    });

    var swatches = Array.prototype.slice.call(wrapper.querySelectorAll('[data-pc-color]'));

    function pintarGaleria(data) {
      var track = card.querySelector('[data-pc-track]');

      // Tarjeta de una sola foto (sin galería): se cambia la imagen y ya.
      if (!track) {
        var unica = card.querySelector('.product-card-image');
        if (unica && data.imgs.length) {
          unica.setAttribute('src', data.imgs[0]);
          unica.setAttribute('alt', data.titulo + ' ' + data.color);
        }
        var ligaFoto = card.querySelector('.product-card-image-container-wrapper a');
        if (ligaFoto) ligaFoto.setAttribute('href', data.url);
        return;
      }

      // Se reconstruye el track porque el número de fotos cambia entre colores.
      // Los listeners viven en el track, no en los slides, así que sobreviven.
      track.innerHTML = data.imgs
        .map(function (src, i) {
          return (
            '<a class="pc-gallery-slide" href="' +
            escAttr(data.url) +
            '" data-pc-slide' +
            (i === 0 ? '' : ' tabindex="-1"') +
            '>' +
            '<img class="product-card-image" src="' +
            escAttr(src) +
            '" alt="' +
            escAttr(data.titulo + ' ' + data.color) +
            '" loading="lazy" fetchpriority="' +
            (i === 0 ? 'auto' : 'low') +
            '">' +
            '</a>'
          );
        })
        .join('');

      if (track.pcGallery) track.pcGallery.refresh();
    }

    function aplicarColor(data) {
      pintarGaleria(data);

      var titulo = card.querySelector('.product-card-title a');
      if (titulo) {
        titulo.textContent = data.titulo;
        titulo.setAttribute('href', data.url);
      }

      var material = card.querySelector('.product-card-material a');
      if (material) {
        material.textContent = data.material || '';
        material.setAttribute('href', data.url);
      }

      var precio = card.querySelector('.product-card-price-wrapper');
      if (precio) precio.innerHTML = data.precio;

      // Las estrellas se crean y se destruyen: un color sin reseñas no debe
      // heredar la calificación del anterior. El renglón NO se mueve porque el
      // subgrid lo tiene reservado para toda la fila de tarjetas (ver
      // component-product-card.css §4), y la posición la da `grid-row` por
      // clase, no el orden en el DOM.
      var info = card.querySelector('.product-card-info');
      var rating = card.querySelector('.product-card-rating');
      if (data.rating) {
        if (!rating && info) {
          rating = document.createElement('div');
          rating.className = 'product-card-rating';
          info.appendChild(rating);
        }
        if (rating) rating.innerHTML = data.rating;
      } else if (rating) {
        rating.remove();
      }

      swatches.forEach(function (swatch) {
        var activo = swatch.getAttribute('data-pc-color') === String(data.id);
        swatch.classList.toggle('is-selected', activo);
        if (activo) {
          swatch.setAttribute('aria-current', 'true');
        } else {
          swatch.removeAttribute('aria-current');
        }
      });
    }

    swatches.forEach(function (swatch) {
      swatch.addEventListener('click', function (event) {
        // Deja pasar pestaña nueva, botón central y cualquier combinación con
        // modificador: ahí el usuario SÍ quiere navegar.
        if (
          event.defaultPrevented ||
          event.button !== 0 ||
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey
        ) {
          return;
        }

        var data = porId[swatch.getAttribute('data-pc-color')];
        if (!data) return; // sin datos: que navegue como siempre

        event.preventDefault();
        aplicarColor(data);
      });
    });
  }

  /* -----------------------------------------------------------------------
     3. Arranque
     ----------------------------------------------------------------------- */

  function initAll(scope) {
    var container = scope || document;

    var tracks = container.querySelectorAll('[data-pc-track]');
    Array.prototype.forEach.call(tracks, initGallery);

    var cards = container.querySelectorAll('.product-card');
    Array.prototype.forEach.call(cards, initSwatches);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  // La grid se re-renderiza al aplicar filtros (facets.js reemplaza el HTML).
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  if ('MutationObserver' in window) {
    var grid = document.getElementById('ProductGridContainer');
    if (grid) {
      new MutationObserver(function () {
        initAll(grid);
      }).observe(grid, { childList: true, subtree: true });
    }
  }
})();
